// hooks/useInternationalAddress.js

import { useState, useCallback, useMemo } from 'react';
import { COUNTRY_LIST, getCountryConfig } from '../services/address/countryConfig';
import { getProvinceOptions } from '../services/address/countryData';
import { validateField, normalizeField, formatForBackend } from '../../utils/addressHelpers';

export const useInternationalAddress = (initialCountry = 'IT') => {
    // Stato
    const [selectedCountry, setSelectedCountry] = useState(initialCountry);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    
    // Configurazione corrente
    const countryConfig = useMemo(() => getCountryConfig(selectedCountry), [selectedCountry]);
    
    // Opzioni province (se disponibili)
    const provinceOptions = useMemo(() => {
        const provinceField = countryConfig.fields.find(f => f.type === 'select');
        if (!provinceField?.source) return null;
        return getProvinceOptions(provinceField.source);
    }, [countryConfig]);
    
    // Inizializza i campi con valori vuoti
    const initializeForm = useCallback((initialValues = {}) => {
        const initialData = {};
        countryConfig.fields.forEach(field => {
            initialData[field.name] = initialValues[field.name] || '';
        });
        setFormData(initialData);
        setErrors({});
        setTouched({});
    }, [countryConfig]);
    
    // Aggiorna un campo
    const updateField = useCallback((fieldName, value) => {
        const normalizedValue = normalizeField(selectedCountry, fieldName, value);
        
        // ✅ Previeni aggiornamenti se il valore non è cambiato
        setFormData(prev => {
            if (prev[fieldName] === normalizedValue) return prev;
            return { ...prev, [fieldName]: normalizedValue };
        });
        
        setTouched(prev => {
            if (prev[fieldName]) return prev;
            return { ...prev, [fieldName]: true };
        });
        
        const validation = validateField(countryConfig, fieldName, normalizedValue);
        if (!validation.valid) {
            setErrors(prev => ({ ...prev, [fieldName]: validation.message }));
        } else {
            setErrors(prev => {
                if (!prev[fieldName]) return prev;
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    }, [selectedCountry, countryConfig]);
    
    // Valida tutti i campi
    const validateAll = useCallback(() => {
        const newErrors = {};
        
        countryConfig.fields.forEach(field => {
            const value = formData[field.name] || '';
            const validation = validateField(countryConfig, field.name, value);
            if (!validation.valid) {
                newErrors[field.name] = validation.message;
            }
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [countryConfig, formData]);
    
    // Resetta il form
    const resetForm = useCallback(() => {
        initializeForm();
    }, [initializeForm]);
    
    // Cambia paese
    const changeCountry = useCallback((newCountry) => {
        setSelectedCountry(newCountry);
        initializeForm();
    }, [initializeForm]);
    
    // Ottieni i dati formattati per il backend
    const getBackendData = useCallback(() => {
        return formatForBackend(selectedCountry, formData);
    }, [selectedCountry, formData]);
    
    // Verifica se il form è valido
    const isValid = useMemo(() => {
        return Object.keys(errors).length === 0 && 
        countryConfig.fields.every(field => {
            if (!field.required) return true;
            const value = formData[field.name];
            return value && value.trim() !== '';
        });
    }, [errors, formData, countryConfig]);
    
    return {
        // Stato
        selectedCountry,
        countryConfig,
        formData,
        errors,
        touched,
        isValid,
        provinceOptions,
        supportedCountries: COUNTRY_LIST,
        
        // Azioni
        updateField,
        validateAll,
        resetForm,
        changeCountry,
        getBackendData,
        initializeForm
    };
};
